import { Box, Slider, Stack, Typography } from '@mui/material';

import { InputLabel } from '@/components/form/baseInputs/InputLabel';
import { FormAwareFormattedNumberInput } from '@/components/form/formAwareInputs/FormAwareFormattedNumberInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { useFormContext } from '@/components/react-hook-form';

import { SlideConfigFormShape } from './SlideConfigForm.types';

/**
 * Restrict the wrapper to fields whose form-state value is a string —
 * the existing percent-input convention in this form is to store the
 * value as a 0–100 string so '' can mean "not set" / use placeholder.
 */
type StringFieldName = {
  [K in keyof SlideConfigFormShape]: SlideConfigFormShape[K] extends string
    ? K
    : never;
}[keyof SlideConfigFormShape];

/**
 * Discriminated config for the editable readout. Each variant picks
 * the matching existing form-aware input so we get its full UX
 * (formatting, decimal scale, adornments) without re-inventing it.
 */
type SliderInputConfig =
  | { kind: 'number'; unit: string; decimalScale?: number }
  | { kind: 'percent'; decimalScale?: number; maxValue?: number };

interface FormAwareSliderProps {
  label: string;
  fieldName: StringFieldName;
  min: number;
  max: number;
  /**
   * Value the slider thumb sits at when the form field is empty. The
   * slider does not write this back unless the user drags — keeping
   * the form field at '' so the readout input stays empty until the
   * user opts in.
   */
  fallbackValue: number;
  /**
   * Slider step. Float steps (e.g. 0.1) are rounded to the step's
   * decimal precision before being written back so the form value
   * doesn't accumulate float drift like `0.30000000000000004`.
   */
  step?: number;
  /**
   * Type of editable readout rendered alongside the slider. The input
   * is bound to the same form field so the slider drag and the typed
   * value stay in lockstep.
   */
  input: SliderInputConfig;
  disabled?: boolean;
}

/**
 * MUI Slider paired with an existing form-aware editable input
 * (FormAwareFormattedNumberInput for `kind: 'number'`,
 * FormAwarePercentInput for `kind: 'percent'`). Both write to the
 * same form field so dragging and typing stay in sync.
 *
 * The form field is stored as a string so '' is distinguishable from
 * a valid 0 — that lets the input render an empty state on first
 * load while the slider thumb still sits at `fallbackValue` for a
 * sensible drag-start position.
 */
export function FormAwareSlider({
  label,
  fieldName,
  min,
  max,
  fallbackValue,
  step = 1,
  input,
  disabled = false,
}: FormAwareSliderProps) {
  const { control, watch, setValue } = useFormContext<SlideConfigFormShape>();
  const raw = watch(fieldName);
  const numeric = raw === '' ? fallbackValue : Number(raw);

  // Number of decimal places implied by the step — used to round
  // outgoing slider values so float-step drags don't store e.g.
  // "0.30000000000000004" in the form state, and as the default for
  // the editable input's decimalScale.
  const stepDecimals = (() => {
    const parts = String(step).split('.');
    return parts[1]?.length ?? 0;
  })();

  // The form initializes these fields to real defaults (see
  // `slideConfigDefaultValues`), mirroring how the color inputs in
  // this form handle defaults — so no placeholder is needed.
  const decimalScale = input.decimalScale ?? stepDecimals;
  const validation = {
    range: (value: SlideConfigFormShape[keyof SlideConfigFormShape]) =>
      value === '' || (Number(value) >= min && Number(value) <= max)
        ? undefined
        : `Enter a value from ${min} to ${max}`,
  };

  const editableInput =
    input.kind === 'number' ? (
      <FormAwareFormattedNumberInput<SlideConfigFormShape>
        control={control}
        fieldName={fieldName}
        label={label}
        hideLabel
        decimalScale={decimalScale}
        validation={validation}
        endAdornment={
          <Typography variant="body-xs" component="span">
            {input.unit}
          </Typography>
        }
        disabled={disabled}
      />
    ) : (
      <FormAwarePercentInput<SlideConfigFormShape>
        control={control}
        fieldName={fieldName}
        label={label}
        hideLabel
        decimalScale={decimalScale}
        maxValue={input.maxValue ?? max}
        validation={validation}
        disabled={disabled}
      />
    );

  return (
    <Stack spacing={0.5}>
      {/* InputLabel matches the typography + spacing of the other
          FormAware* inputs so the slider blends into the form. The
          editable input lives in the labelIconEnd slot; the Box
          constrains its width so it sits inline next to the label
          without pushing the row wide. 80px is tight but fits the
          widest values we accept (3 digits like "200" or "66.0"). */}
      <InputLabel labelIconEnd={<Box sx={{ width: 80 }}>{editableInput}</Box>}>
        {label}
      </InputLabel>
      <Slider
        value={numeric}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(_, value) => {
          const next = Array.isArray(value) ? (value[0] ?? min) : value;
          const rounded = Number(next.toFixed(stepDecimals));
          setValue(fieldName, String(rounded), { shouldDirty: true });
        }}
      />
    </Stack>
  );
}
